import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  GenerationKoma,
  GenerationPlantData,
} from '@ebalance/proto/bffuser/v1/balance_model_pb'
import type { GetGenerationDetailResponse } from '@ebalance/proto/bffuser/v1/balance_service_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'

import type { GateCloseFrontier } from '~/models/gateClose'
import { gateCloseFrontierFromProto } from '~/models/gateClose'
import { komaIndexToTime } from '~/models/komaToTime'
import type { BgConfig } from '~/pages/BalanceGenerationDetail/constants'

export type HatsuDetailKomaData = {
  koma: number
  time: string
  genPlan: number
  genActual: number | null
  /**
   * 発電 同時同量 誤差率(%)。backend が (実績−計画)/計画×100 で算出した値
   * (bg_komas.gen_error_rate、balanceentity.ComputeErrorRate)。計画0コマは 0.0%、
   * 計画/実績が欠測のコマは null。frontend では再計算しない (需要とは符号が逆なので共通 KomaTable の再計算式では誤表示になる)。
   */
  genErrorRate: number | null
}

export type BreakdownSource = {
  /** plant_code (発電所別内訳の行キー)。発電形式 (HJKS, #1998) はプラント粒度では未提供。 */
  key: string
  label: string
  value: number
  pct: number
}

export type BreakdownResult = {
  sources: BreakdownSource[]
  total: number
}

export type GenerationDetailData = {
  bgConfig: BgConfig
  komas: HatsuDetailKomaData[]
  breakdown: BreakdownResult
  gcKoma: number
  gateCloseFrontier: GateCloseFrontier | undefined
  currentKoma: number
}

/**
 * google.type.Decimal → number | null
 * undefined (= backend 未結線 / 欠損) は null を返す。
 */
function decimalToNumber(d: Decimal | undefined): number | null {
  if (d === undefined) return null
  const n = Number(d.value)
  return Number.isNaN(n) ? null : n
}

/**
 * plantDailyTotal sums a single plant's 48-koma gen_actual (kWh). Komas with
 * no value (未確定/欠損) contribute 0.
 */
function plantDailyTotal(komas: GenerationKoma[]): number {
  return komas.reduce((sum, k) => sum + (decimalToNumber(k.genActual) ?? 0), 0)
}

/**
 * computePlantBreakdown builds a per-plant breakdown (発電所別内訳) from the
 * raw plant list. This replaces the mock's power-source (HJKS) breakdown:
 * plant-level HJKS category is out of scope (#1998, BG-level only), but the
 * per-plant contribution to the BG total is exactly what #2000 backs.
 *
 * 発電所単位の計画折れ線・計画比はここでも描画しない (実績構成比のみ)。source は
 * 48 点データの発電所別計画値に確定 (2026-07-14 PdM) だが、supply_48_data の
 * 発電所単位 schema が未整備のため未結線 (backend GenerationPlantOutput.PlanSlots
 * も all-None のまま)。
 *
 * [既知の spec drift] doji-hatsu.md AC-BDH-021 / 同 User Story はこのペインを
 * 「発電形式内訳」と呼んでおり、spec が要求するのは HJKS 9 区分の内訳ペイン。
 * 本関数が実際に作るのは発電所別の内訳で、i18n タイトル
 * (`balanceGenerationDetail.breakdown.title` = 「発電所別内訳」) は実体に
 * 正直な名前を付けているだけで、この drift を隠すものではない。 spec が求める
 * 発電形式内訳ペインの実装は #2807 (電源ユニットマスタ、値の集約) 待ち。
 */
export function computePlantBreakdown(
  plants: GenerationPlantData[],
): BreakdownResult {
  const sources: BreakdownSource[] = plants.map((p) => ({
    key: p.plantCode,
    label: p.plantName || p.plantCode,
    value: plantDailyTotal(p.komas),
    pct: 0,
  }))
  const total = sources.reduce((s, src) => s + src.value, 0)
  const withPct = sources.map((s) => ({
    ...s,
    pct: total > 0 ? (s.value / total) * 100 : 0,
  }))
  return { sources: withPct, total }
}

function convertKoma(
  index: number,
  plants: GenerationPlantData[],
  bgKomas: GenerationKoma[],
): HatsuDetailKomaData {
  let sum = 0
  let anyPresent = false
  for (const p of plants) {
    const k = p.komas[index]
    const v = k === undefined ? null : decimalToNumber(k.genActual)
    if (v !== null) {
      sum += v
      anyPresent = true
    }
  }
  // gen_plan は BG 単位の発電計画値 (bg_komas 由来、backend が発電販売計画から解決、
  // #2000 task 1)。plant 単位の合算ではない。未提供コマは 0 表示にフォールバック。
  // （発電所単位の計画折れ線・計画比は描画しない: source は 48 点データの発電所別
  //  計画値に確定 (2026-07-14 PdM) だが supply_48_data の発電所単位 schema 整備待ち。）
  const genPlan = decimalToNumber(bgKomas[index]?.genPlan) ?? 0
  return {
    koma: index + 1,
    time: komaIndexToTime(index),
    genPlan,
    genActual: anyPresent ? sum : null,
    // 誤差率は backend 算出値をそのまま保持 (再計算しない。型 doc 参照)。
    genErrorRate: decimalToNumber(bgKomas[index]?.genErrorRate),
    // 発電形式内訳 (HJKS) はプラント粒度では非対象 (#1998、BG 単位のみ)。
    // 発電形式トグルの再実装自体が #2807 とセットの別スコープ (#7537 では
    // 対象外) のため、この画面はプレースホルダーの内訳フィールドを持たない。
  }
}

/**
 * convertGenerationDetailResponse converts the real GetGenerationDetailResponse
 * into the page's display data shape. The combined koma series sums gen_actual
 * across every plant under the requested BG (a koma is null only when no
 * plant reports a value there); breakdown is computed here in the frontend
 * from that actual data (#1996 dashboard 接続と同じ判断: backend は集計値を
 * 返さず、frontend 側で actual から算出する)。
 */
export function convertGenerationDetailResponse(
  resp: GetGenerationDetailResponse,
  bgCode: string,
): GenerationDetailData {
  const plants = resp.plants
  const bgKomas = resp.bgKomas
  const komas = Array.from({ length: 48 }, (_, i) =>
    convertKoma(i, plants, bgKomas),
  )
  const breakdown = computePlantBreakdown(plants)

  const representativeAreaCode: AreaCode | undefined = plants[0]?.areaCode

  const bgConfig: BgConfig = {
    code: bgCode,
    // bg_name is not exposed by GetGenerationDetail (#2000 スコープ外) — bgCode
    // をそのまま表示名にフォールバックする (index.tsx の既存フォールバックと整合)。
    name: bgCode,
    procurementType: '',
    areaCode: representativeAreaCode ?? (0 as AreaCode),
    type: 'mixed',
    // capacity: プラント容量は GetGenerationDetail が返さない (#2000 残タスク)。
    capacity: 0,
  }

  return {
    bgConfig,
    komas,
    breakdown,
    gcKoma: resp.gcKoma,
    gateCloseFrontier: gateCloseFrontierFromProto(resp.gateCloseFrontier),
    currentKoma: resp.currentKoma,
  }
}
